/**
 * AccordionControls component.
 * One toggle that expands or collapses every item of the enclosing accordion.
 * @module components/ui/Accordion/AccordionControls
 */

import type { ReactNode } from "react";

import { Button } from "@/components/ui/Button";
import { EditLockExempt } from "@/components/ui/EditLock";

import { useAccordion } from "./context";

/** Below this many items there is nothing worth opening or closing in bulk. */
export const MIN_ITEMS_FOR_TOGGLE = 3;

export interface AccordionControlsProps {
  /** Accordion ID, for the aria relationship */
  accordionId: string;
  /** The accordion's own heading, sharing the row with the toggle. */
  heading?: ReactNode;
}

/**
 * Expand all or Collapse all, by state, for the enclosing accordion. Rendered
 * by `Accordion` itself through its `controls` prop, and absent until the
 * accordion has enough items to need it.
 */
export function AccordionControls({ accordionId, heading = undefined }: AccordionControlsProps) {
  const { expandAll, collapseAll, openCount, itemCount } = useAccordion();

  if (itemCount < MIN_ITEMS_FOR_TOGGLE && !heading) {
    return null;
  }
  const allOpen = itemCount > 0 && openCount >= itemCount;

  return (
    <EditLockExempt>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">{heading}</div>
        {itemCount >= MIN_ITEMS_FOR_TOGGLE ? (
          <Button
            variant="link"
            size="sm"
            icon={allOpen ? "arrows-in-simple" : "arrows-out-simple"}
            onClick={allOpen ? collapseAll : expandAll}
            aria-controls={accordionId}
            data-testid="accordion-toggle-all"
          >
            {allOpen ? "Collapse all" : "Expand all"}
          </Button>
        ) : null}
      </div>
    </EditLockExempt>
  );
}

export default AccordionControls;
